import { useEffect, useRef, useState } from 'react';
import {
  type FamilyManagementService, type FamilyProfile, type FamilyProfilesState, type NewFamilyMember,
  familyManagementService,
} from '../familyManagement';
import { SidebarIcon } from './SidebarIcon';
import { AddFamilyMemberDialog } from './AddFamilyMemberDialog';
import { FamilyDialog } from './FamilyDialog';
import { useFamilyFocusMode } from './useFamilyFocusMode';
import './FamilyProfiles.css';

const Plus = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg>;
const Check = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>;
const Close = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M7 17 17 7" /></svg>;
const initials = (name: string) => name.trim().split(/\s+/).filter(Boolean)
  .filter((_, index, parts) => index === 0 || index === parts.length - 1)
  .map((part) => Array.from(part)[0]).join('').toLocaleUpperCase('de-CH');

const primaryState = (id: string, fullName: string): FamilyProfilesState => ({
  profiles: [{ id, fullName, isPrimary: true, canActivate: true, canRemove: false }],
  activeProfileId: id,
  canAdd: false,
});

function checkProfiles(state: FamilyProfilesState) {
  const primary = state.profiles.filter((profile) => profile.isPrimary);
  if (primary.length !== 1 || new Set(state.profiles.map((profile) => profile.id)).size !== state.profiles.length ||
      !state.profiles.every((profile) => profile.id && profile.fullName.trim()) ||
      !state.profiles.some((profile) => profile.id === state.activeProfileId && profile.canActivate)) {
    throw new Error('Invalid family profile response');
  }
  return state;
}

export function FamilyProfiles({ primaryName, primaryProfileId, onLogout, service = familyManagementService }: {
  primaryName: string;
  primaryProfileId: string;
  onLogout: () => void;
  service?: FamilyManagementService;
}) {
  const [state, setState] = useState(() => primaryState(primaryProfileId, primaryName));
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<'add' | FamilyProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [removeError, setRemoveError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const operationInProgress = useRef(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const active = state.profiles.find((profile) => profile.id === state.activeProfileId)!;
  const hasFamilyMembers = state.profiles.some((profile) => !profile.isPrimary);
  const hasAdditionalProfiles = state.profiles.some((profile) => !profile.isPrimary && profile.canActivate);
  useFamilyFocusMode(containerRef);

  useEffect(() => {
    mounted.current = true;
    generation.current += 1;
    const controller = new AbortController();
    setState(primaryState(primaryProfileId, primaryName));
    setOpen(false);
    setModal(null);
    setError('');
    setNotice('');
    setBusy(false);
    setLoading(false);
    operationInProgress.current = false;
    if (!service.unavailableReason) {
      setLoading(true);
      service.loadProfiles(controller.signal).then(checkProfiles).then((profiles) => {
        if (!controller.signal.aborted) setState(profiles);
      }).catch(() => {
        if (!controller.signal.aborted) setError('Die verfügbaren Profile konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    }
    return () => { mounted.current = false; generation.current += 1; controller.abort(); };
  }, [primaryName, primaryProfileId, service]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); toggleRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const openAdd = () => {
    setOpen(false);
    setNotice('');
    setModal('add');
  };

  const add = async (input: NewFamilyMember) => {
    if (!state.canAdd || service.unavailableReason) throw new Error('Not authorized');
    const operationGeneration = generation.current;
    const profile = await service.addMember(input);
    if (profile.isPrimary || !profile.id || !profile.fullName?.trim() || state.profiles.some((item) => item.id === profile.id)) {
      throw new Error('Invalid family member response');
    }
    if (!mounted.current || generation.current !== operationGeneration) return;
    // Adding a verified link never changes the active profile.
    setState((current) => ({ ...current, profiles: [...current.profiles, profile] }));
    setNotice('Das Familienmitglied wurde hinzugefügt. Ihr aktives Profil bleibt bestehen.');
  };

  const activate = async (profile: FamilyProfile) => {
    if (operationInProgress.current) return;
    if (profile.id === state.activeProfileId) { setOpen(false); toggleRef.current?.focus(); return; }
    if (!profile.canActivate || service.unavailableReason) return;
    operationInProgress.current = true;
    const operationGeneration = generation.current;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await service.activateProfile(profile.id);
      if (!mounted.current || generation.current !== operationGeneration) return;
      setState((current) => ({ ...current, activeProfileId: profile.id }));
      setOpen(false);
      toggleRef.current?.focus();
    } catch {
      if (mounted.current && generation.current === operationGeneration) setError('Das Profil konnte nicht gewechselt werden. Ihr bisheriges Profil bleibt aktiv.');
    } finally {
      if (generation.current === operationGeneration) {
        operationInProgress.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };

  const remove = async (profile: FamilyProfile) => {
    if (operationInProgress.current || profile.isPrimary || !profile.canRemove || service.unavailableReason) return;
    operationInProgress.current = true;
    const operationGeneration = generation.current;
    setBusy(true);
    setRemoveError('');
    try {
      await service.removeMember(profile.id);
      if (!mounted.current || generation.current !== operationGeneration) return;
      setState((current) => ({
        ...current, profiles: current.profiles.filter((item) => item.id !== profile.id),
        activeProfileId: current.activeProfileId === profile.id
          ? current.profiles.find((item) => item.isPrimary)!.id : current.activeProfileId,
      }));
      setModal(null);
      setOpen(false);
      setNotice('Die Familienverknüpfung wurde entfernt. Patientenakte und Termine bleiben erhalten.');
    } catch {
      if (mounted.current && generation.current === operationGeneration) setRemoveError('Das Familienmitglied konnte nicht entfernt werden. Die Verknüpfung bleibt bestehen. Bitte versuchen Sie es erneut.');
    } finally {
      if (generation.current === operationGeneration) {
        operationInProgress.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };

  return (
    <>
      <div className="family-profiles" ref={containerRef}>
        {notice && <p role="status" className="family-profiles__notice">{notice}</p>}
        {error && <p role="alert" className="family-profiles__error">{error}</p>}
        <button ref={addRef} type="button" className="family-profiles__add" onClick={openAdd}
          disabled={busy || loading} aria-haspopup="dialog">
          <Plus /><span>Familienmitglied hinzufügen</span>
        </button>
        <div className="family-profiles__anchor">
          {open && hasAdditionalProfiles && (
            <section id="family-profile-options" className="family-profiles__dropdown" aria-labelledby="family-profile-heading">
              <h2 id="family-profile-heading">PROFIL WECHSELN</h2>
              <ul className="family-profiles__list">
                {state.profiles.map((profile) => (
                  <li key={profile.id} className={profile.id === active.id ? 'family-profiles__row family-profiles__row--active' : 'family-profiles__row'}>
                    <button type="button" className="family-profiles__select" onClick={() => activate(profile)}
                      disabled={busy || !profile.canActivate} aria-pressed={profile.id === active.id}
                      aria-label={profile.fullName} title={!profile.canActivate ? 'Freigabe durch die Praxis erforderlich' : undefined}>
                      <span className="family-profiles__initials" aria-hidden="true">{initials(profile.fullName)}</span>
                      <span className="family-profiles__name">{profile.fullName}</span>
                      {profile.id === active.id && <span className="family-profiles__check"><Check /></span>}
                    </button>
                    {!profile.isPrimary && profile.canRemove && <button type="button" className="family-profiles__remove"
                      disabled={busy} aria-label={`${profile.fullName} entfernen`} onClick={() => {
                        setRemoveError(''); setOpen(false); setModal(profile);
                      }}><Close /></button>}
                  </li>
                ))}
              </ul>
              {service.unavailableReason && <p className="family-profiles__availability">{service.unavailableReason}</p>}
              <button type="button" className="family-profiles__add family-profiles__add--dropdown" onClick={openAdd}
                disabled={busy || loading} aria-haspopup="dialog"><Plus /><span>Weiteres Mitglied hinzufügen</span></button>
            </section>
          )}
          <div className={`family-profiles__tile${hasAdditionalProfiles ? '' : ' family-profiles__tile--single'}`}>
            <span className="family-profiles__avatar"><SidebarIcon name="profile" /></span>
            <div className="family-profiles__identity">{hasFamilyMembers && <span>Aktives Profil</span>}<strong>{active.fullName}</strong></div>
            {hasAdditionalProfiles && <button ref={toggleRef} type="button" className="family-profiles__toggle" aria-label="Profilauswahl"
              disabled={busy || loading} aria-expanded={open} aria-controls={open ? 'family-profile-options' : undefined}
              onClick={() => setOpen((current) => !current)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>
            </button>}
            <button type="button" className="family-profiles__logout" aria-label="Abmelden" onClick={onLogout} disabled={busy}>
              <SidebarIcon name="logout" />
            </button>
          </div>
        </div>
      </div>
      {modal === 'add' && <AddFamilyMemberDialog onAdd={add} returnFocusRef={addRef}
        unavailableReason={service.unavailableReason || (!state.canAdd ? 'Sie haben keine Berechtigung, Familienmitglieder hinzuzufügen.' : undefined)}
        onClose={() => setModal(null)} />}
      {modal && modal !== 'add' && (
        <FamilyDialog title="Familienmitglied entfernen?" busy={busy}
          returnFocusRef={toggleRef}
          fallbackFocusRef={addRef}
          description={`Möchten Sie ${modal.fullName} wirklich aus Ihrer Familienverwaltung entfernen?`}
          onClose={() => setModal(null)}>
          <p className="family-dialog__notice">Die Patientenakte und bestehende Termine werden dadurch nicht gelöscht.</p>
          {removeError && <p role="alert" className="family-dialog__error">{removeError}</p>}
          <div className="family-dialog__actions">
            <button type="button" className="family-dialog__secondary" data-initial-focus="" disabled={busy}
              onClick={() => setModal(null)}>Abbrechen</button>
            <button type="button" className="family-dialog__caution" disabled={busy} onClick={() => remove(modal)}>
              {busy ? 'Wird entfernt…' : 'Familienmitglied entfernen'}
            </button>
          </div>
        </FamilyDialog>
      )}
    </>
  );
}
